import { nodeDefsForKind } from '../graph/nodeRegistry.js';
import { applyTransform } from './viewportState.js';
import type { GraphNode } from '../graph/nodeDefs.js';
import { getEl } from '../dom.js';
import type { NodeInteractionViewState } from './nodeInteractions.js';
import { createEdgeRendererController } from './edgeRenderer.js';
import type { EdgeVisualStateController } from './edgeVisualState.js';
import type { ConnectionStateController } from './connectionState.js';
import { createEdgeDetachController } from './edgeDetach.js';
import { renderNodeCanvas } from './nodeCanvas.js';
import { applyConnectionState, applyValidationState, updateSelectionState } from './graphViewState.js';
import { createPortDragHandler } from './portInteractions.js';
import { createNodeDragHandler } from './nodeDrag.js';
import { getDotWorldPosFor } from './portGeometry.js';
import type { EdgeHintController } from './edgeHint.js';
import type { AppState } from '../state/appState.js';
import type { EditorCommands } from '../editor/commands.js';
import type { GraphMutations } from '../editor/graphMutations.js';
import type { EditorConnectionMutations } from '../editor/connectionMutations.js';
import type { CommandBatch } from '../editor/commandHistory.js';
import { recordContextFieldsForGraph } from '../state/recordContextSchema.js';
import {
  deleteDataOutput,
  renameDataInput,
  renameDataOutput,
  setDataOutputType,
} from '../editor/dataOutputBindings.js';
import { bakeSvgShapeNodeWasm } from 'fluveria-engine-wasm';
import { errorMessageOf, showAppError } from './errors.js';

export interface NodeEditorController {
  createNode(type: string, x: number, y: number): GraphNode | null;
  deleteNode(id: string): void;
  updateNodePositions(): void;
  renderGraph(): void;
  setSelectedNode(nodeId: string | null): void;
  refreshGraphView(): void;
  renderConnections(): void;
  updatePendingConnection(): void;
}

interface NodeEditorDeps {
  appState: AppState;
  commands: EditorCommands;
  graphMutations: GraphMutations;
  connectionMutations: EditorConnectionMutations;
  connectionState: ConnectionStateController;
  edgeVisualState: EdgeVisualStateController;
  edgeHint: EdgeHintController;
  nodeInteractionViewState: NodeInteractionViewState;
  forceRender: () => void;
  scheduleRender: () => void;
  updateParams: () => void;
  commandBatch: CommandBatch;
}

export function createNodeEditorController(deps: NodeEditorDeps): NodeEditorController {
  const edgeRenderer = createEdgeRendererController({
    visualState: deps.edgeVisualState,
  });

  function currentNodeDefs() {
    return nodeDefsForKind(deps.appState.editorGraphKind);
  }

  function getDotWorldPos(nodeId: string, portIdx: number, isOut: boolean): { x: number; y: number } {
    return getDotWorldPosFor(deps.appState.nodes, nodeId, portIdx, isOut);
  }

  const controller: NodeEditorController = {
    createNode(type: string, x: number, y: number): GraphNode | null {
      const node = deps.graphMutations.createNodeWithHistory(type, x, y);
      if (!node) return null;
      controller.setSelectedNode(node.id);
      controller.renderGraph();
      return node;
    },

    deleteNode(id: string): void {
      if (!deleteDataOutput(deps.appState, deps.commands, deps.commandBatch, id)) {
        deps.graphMutations.deleteNodeWithHistory(id);
      }
      controller.renderGraph();
      deps.updateParams();
      deps.scheduleRender();
    },

    updateNodePositions(): void {
      const w = getEl('graph-canvas-wrapper');
      if (!w) return;
      deps.appState.nodes.forEach(node => {
        const el = getEl(`node-${node.id}`);
        if (el) {
          el.style.left = node.x + 'px';
          el.style.top = node.y + 'px';
        }
      });
      controller.renderConnections();
    },

    renderGraph(): void {
      applyTransform();
      renderNodeCanvas(deps.appState.nodes, {
        edges: deps.appState.edges,
        selectedNode: deps.appState.selectedNode,
        deleteNode: controller.deleteNode,
        setupPortDrag,
        setupNodeDrag,
        setNodeData: (nodeId, field, value) => {
          const output = deps.appState.editorGraphKind === 'data'
            ? deps.appState.nodes.find(node => node.id === nodeId && node.type === 'Output')
            : undefined;
          const input = deps.appState.editorGraphKind === 'data'
            ? deps.appState.nodes.find(node => node.id === nodeId && node.type === 'Input')
            : undefined;
          if (output && field === 'inputType') {
            if (setDataOutputType(
              deps.appState,
              deps.commands,
              deps.commandBatch,
              nodeId,
              String(value),
            )) controller.renderGraph();
            return;
          }
          if (!output || field !== 'inputName') {
            if (input && field === 'name') {
              if (!renameDataInput(deps.appState, deps.commands, deps.commandBatch, nodeId, String(value))) controller.renderGraph();
              return;
            }
            deps.commands.setNodeData(nodeId, field, value);
            return;
          }
          if (!renameDataOutput(
            deps.appState,
            deps.commands,
            deps.commandBatch,
            nodeId,
            String(value),
          )) {
            controller.renderGraph();
            return;
          }
          deps.updateParams();
          controller.renderGraph();
        },
        nodeFieldCommands: deps.commands,
        renderGraph: controller.renderGraph,
        scheduleRender: deps.scheduleRender,
        updateParams: deps.updateParams,
        runtimeState: deps.appState.runtimeState,
        dataSources: deps.appState.dataSources,
        shapes: { ...deps.appState.builtInShapes, ...deps.appState.shapes },
        invokeNodeAction(node, action) {
          if (action !== 'convert-shape-to-path' || node.type !== 'Shape') return;
          try {
            if (shapeSizeUsesLayerContext(deps.appState, node.id)) {
              throw new Error('Width and height must be static before converting a Shape to Path.');
            }
            const baked = bakeSvgShapeNodeWasm({
              nodes: deps.appState.nodes,
              edges: deps.appState.edges,
              graphId: null,
              params: deps.appState.params,
              layerSize: [1, 1],
              shapeUnit: 'px',
              pixelsPerShapeUnit: 1,
              shapes: { ...deps.appState.builtInShapes, ...deps.appState.shapes },
              fonts: {},
            }, node.id) as Record<string, unknown>;
            const { type: _bakedType, ...data } = baked;
            deps.commands.replaceNode(node.id, { ...node, type: 'Path', data } as GraphNode);
            deps.updateParams();
            controller.renderGraph();
            deps.scheduleRender();
          } catch (error) {
            showAppError({
              title: 'Could not convert Shape',
              message: errorMessageOf(error),
              source: 'Shape → Path',
            });
          }
        },
        queryGraphs: deps.appState.queryGraphs,
        recordContextFields: recordContextFieldsForGraph(deps.appState, deps.appState.activeDataGraphId),
        setRuntimeStateReference(node, field, stateName) {
          const declaration = deps.appState.runtimeState[stateName];
          if (!declaration) return;
          const outgoing = node.type === 'State'
            ? deps.appState.edges.filter(edge => edge.fromNode === node.id)
            : [];
          const incomingValue = node.type === 'Set'
            ? deps.appState.edges.filter(edge => edge.toNode === node.id && edge.toPort === 1)
            : [];
          deps.commandBatch.run(`Set state reference: ${stateName}`, () => {
            if (outgoing.length || incomingValue.length) deps.commands.disconnectEdges([...outgoing, ...incomingValue]);
            deps.commands.setNodeData(node.id, field, stateName);
            deps.commands.setNodeData(node.id, 'valueType', declaration.type);
            if (node.type === 'State' && field === 'name') deps.commands.setNodeData(node.id, 'scope', declaration.scope);
          });
        },
        setLookupSource(node, sourceId) {
          const source = deps.appState.dataSources[sourceId];
          if (!source || (node.type !== 'Lookup' && node.type !== 'SetRecordField')) return;
          const field = source.fields[String(node.data.field ?? '')]
            ? String(node.data.field)
            : Object.keys(source.fields).sort()[0];
          if (!field) return;
          const valueTypeChanged = node.data.valueType !== source.fields[field].type;
          const edges = deps.appState.edges;
          if (node.type === 'SetRecordField') {
            const incomingValue = valueTypeChanged
              ? edges.filter(edge => edge.toNode === node.id && edge.toPort === 1)
              : [];
            deps.commandBatch.run(`Set Record Source: ${sourceId}`, () => {
              if (incomingValue.length) deps.commands.disconnectEdges(incomingValue);
              deps.commands.setNodeData(node.id, 'source', sourceId);
              deps.commands.setNodeData(node.id, 'field', field);
              deps.commands.setNodeData(node.id, 'valueType', source.fields[field].type);
            });
            return;
          }
          const keyTypeChanged = node.data.keyType !== source.keyType;
          deps.commandBatch.run(`Set Lookup Source: ${sourceId}`, () => {
            if (keyTypeChanged) {
              const incoming = edges.filter(edge => edge.toNode === node.id && edge.toPort === 0);
              if (incoming.length) deps.commands.disconnectEdges(incoming);
            }
            if (valueTypeChanged) {
              const outgoing = edges.filter(edge => edge.fromNode === node.id && edge.fromPort === 0);
              if (outgoing.length) deps.commands.disconnectEdges(outgoing);
            }
            deps.commands.setNodeData(node.id, 'source', sourceId);
            deps.commands.setNodeData(node.id, 'field', field);
            deps.commands.setNodeData(node.id, 'keyType', source.keyType);
            deps.commands.setNodeData(node.id, 'valueType', source.fields[field].type);
          });
        },
        setLookupField(node, field) {
          if (node.type !== 'Lookup' && node.type !== 'SetRecordField') return;
          const source = deps.appState.dataSources[String(node.data.source ?? '')];
          const valueType = source?.fields[field]?.type;
          if (!valueType || (node.data.field === field && node.data.valueType === valueType)) return;
          if (node.type === 'SetRecordField') {
            const incomingValue = node.data.valueType !== valueType
              ? deps.appState.edges.filter(edge => edge.toNode === node.id && edge.toPort === 1)
              : [];
            deps.commandBatch.run(`Set Record Field: ${field}`, () => {
              if (incomingValue.length) deps.commands.disconnectEdges(incomingValue);
              deps.commands.setNodeData(node.id, 'field', field);
              deps.commands.setNodeData(node.id, 'valueType', valueType);
            });
            return;
          }
          deps.commandBatch.run(`Set Lookup Field: ${field}`, () => {
            if (node.data.valueType !== valueType) {
              const outgoing = deps.appState.edges.filter(edge => edge.fromNode === node.id && edge.fromPort === 0);
              if (outgoing.length) deps.commands.disconnectEdges(outgoing);
            }
            deps.commands.setNodeData(node.id, 'field', field);
            deps.commands.setNodeData(node.id, 'valueType', valueType);
          });
        },
        nodeDefs: currentNodeDefs(),
      });
      controller.refreshGraphView();
      deps.nodeInteractionViewState.restoreSnapHighlights(deps.connectionState);
      deps.nodeInteractionViewState.restoreHoverState(deps.connectionState);
    },

    setSelectedNode(nodeId: string | null): void {
      deps.appState.selectedNode = nodeId;
      updateSelectionState(deps.appState);
    },

    refreshGraphView(): void {
      applyTransform();
      applyConnectionState(deps.connectionState);
      applyValidationState(deps.appState);
      controller.renderConnections();
      renderPendingConnection();
    },

    renderConnections(): void {
      edgeRenderer.renderConnections(
        deps.connectionState.getVisibleEdges(),
        deps.appState,
        getDotWorldPos,
        edgeHandlers(),
        currentNodeDefs(),
      );
    },

    updatePendingConnection(): void {
      renderPendingConnection();
    },
  };

  const edgeDetach = createEdgeDetachController({
    getEdges: () => deps.appState.edges,
    getDotWorldPos,
    visualState: deps.edgeVisualState,
    disconnectEdgeToPending: (...args) => deps.connectionMutations.disconnectEdgeToPending(...args),
    setPendingConn: next => deps.connectionState.setPendingConn(next),
    refreshRenderedConnections: controller.renderConnections,
    renderGraph: controller.renderGraph,
    scheduleRender: deps.scheduleRender,
    forceRender: deps.forceRender,
  });

  const setupPortDrag = createPortDragHandler({
    connectionState: () => deps.connectionState,
    edgeDetach,
    getDotWorldPos,
    visualState: deps.edgeVisualState,
    reconnectFromInput: (...args) => deps.connectionMutations.reconnectFromInput(...args),
    renderConnections: controller.renderConnections,
    renderGraph: controller.renderGraph,
    renderPendingConnection,
    forceRender: deps.forceRender,
    applyConnectionState: () => applyConnectionState(deps.connectionState),
    applyValidationState: () => applyValidationState(deps.appState),
    restoreSnapHighlights: () => deps.nodeInteractionViewState.restoreSnapHighlights(deps.connectionState),
  });

  const setupNodeDrag = createNodeDragHandler(controller.setSelectedNode);

  function edgeHandlers() {
    return {
      onDisconnect: edgeDetach.disconnectEdgeSocket,
      onPreview: edgeDetach.previewDetachTarget,
      onClearPreview: edgeDetach.clearDetachPreview,
      onShowHint: deps.edgeHint.show,
    };
  }

  function renderPendingConnection(): void {
    edgeRenderer.renderPendingConnection(deps.connectionState.pendingConn, deps.appState, currentNodeDefs());
  }

  return controller;
}

function shapeSizeUsesLayerContext(appState: AppState, shapeNodeId: string): boolean {
  const incoming = new Map<string, string[]>();
  for (const edge of appState.edges) {
    const sources = incoming.get(edge.toNode) ?? [];
    sources.push(edge.fromNode);
    incoming.set(edge.toNode, sources);
  }
  const visit = [...(incoming.get(shapeNodeId) ?? [])];
  const seen = new Set<string>();
  while (visit.length) {
    const nodeId = visit.pop()!;
    if (seen.has(nodeId)) continue;
    seen.add(nodeId);
    const node = appState.nodes.find(candidate => candidate.id === nodeId);
    if (node?.type === 'LayerSize') return true;
    visit.push(...(incoming.get(nodeId) ?? []));
  }
  return false;
}
